// src/theme.js
// ----------------------------------------------------------------------------
// 🎨 Warranty Mitra — Premium Green Theme (matches the landing-page design)
//
//   Palette:  green (#15803d → #16a34a → #22c55e), dark navy (#0a1628)
//   Type:     Sora (headings) + DM Sans (body)
//   Feel:     Rounded 14–20, soft green-tinted shadows, gradient CTAs,
//             bottom-sheet dialogs, mobile-first.
//
// HOW TO USE
//   1. Save as `src/theme.js`
//   2. In `public/index.html` (or `src/index.css`), load the fonts once:
//        <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
//   3. In `src/index.js`:
//        import { ThemeProvider, CssBaseline } from "@mui/material";
//        import theme from "./theme";
//        <ThemeProvider theme={theme}><CssBaseline /><App /></ThemeProvider>
// ----------------------------------------------------------------------------

import { createTheme, alpha } from "@mui/material/styles";

// 🎨 Color tokens (straight from the reference design)
const GREEN = "#15803d";
const GREEN_LIGHT = "#16a34a";
const GREEN_ACCENT = "#22c55e";
const GREEN_DARK = "#0f5c2e";
const GREEN_PALE = "#dcfce7";
const GOLD = "#f59e0b";

const DARK = "#0a1628";
const INK = "#111827";
const INK_2 = "#374151";
const INK_3 = "#6b7280";
const INK_4 = "#9ca3af";
const BORDER = "#e5e7eb";
const SURFACE = "#ffffff";
const SURFACE_ALT = "#f8faf9";
const GRAY_50 = "#f9fafb";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: GREEN,
      dark: GREEN_DARK,
      light: GREEN_LIGHT,
      contrastText: "#ffffff",
    },
    secondary: {
      main: GREEN_ACCENT,
      dark: GREEN_LIGHT,
      light: "#4ade80",
      contrastText: "#ffffff",
    },
    success: { main: GREEN_LIGHT, light: GREEN_ACCENT, dark: GREEN_DARK },
    warning: { main: GOLD, light: "#fbbf24", dark: "#d97706" },
    error: { main: "#dc2626", light: "#ef4444", dark: "#b91c1c" },
    info: { main: "#0ea5e9", light: "#38bdf8", dark: "#0284c7" },
    background: {
      default: SURFACE_ALT,
      paper: SURFACE,
    },
    text: {
      primary: INK,
      secondary: INK_3,
    },
    divider: BORDER,
    // Custom tokens for components
    custom: {
      dark: DARK,
      greenPale: GREEN_PALE,
      greenAccent: GREEN_ACCENT,
      gold: GOLD,
    },
  },

  shape: { borderRadius: 14 },

  typography: {
    fontFamily:
      '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    h1: { fontFamily: '"Sora", sans-serif', fontWeight: 800, letterSpacing: -1 },
    h2: { fontFamily: '"Sora", sans-serif', fontWeight: 800, letterSpacing: -0.5 },
    h3: { fontFamily: '"Sora", sans-serif', fontWeight: 800, letterSpacing: -0.4 },
    h4: { fontFamily: '"Sora", sans-serif', fontWeight: 700, letterSpacing: -0.3 },
    h5: { fontFamily: '"Sora", sans-serif', fontWeight: 700, letterSpacing: -0.2 },
    h6: { fontFamily: '"Sora", sans-serif', fontWeight: 700, letterSpacing: -0.1 },
    subtitle1: { fontFamily: '"Sora", sans-serif', fontWeight: 700 },
    subtitle2: { fontFamily: '"Sora", sans-serif', fontWeight: 600 },
    body1: { fontWeight: 400, color: INK_2, lineHeight: 1.6 },
    body2: { fontWeight: 400, color: INK_3, lineHeight: 1.6 },
    button: {
      fontFamily: '"Sora", sans-serif',
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: 0.2,
    },
    caption: { color: INK_3, fontSize: 12 },
    overline: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      color: GREEN,
    },
  },

  // Soft, green-tinted shadow ladder
  shadows: [
    "none",
    "0 1px 2px rgba(21,128,61,0.04)",
    "0 2px 6px rgba(21,128,61,0.06)",
    "0 4px 12px rgba(21,128,61,0.06)",
    "0 4px 24px rgba(21,128,61,0.08)",
    "0 6px 20px rgba(21,128,61,0.10)",
    "0 8px 24px rgba(21,128,61,0.12)",
    "0 10px 28px rgba(21,128,61,0.13)",
    "0 12px 32px rgba(21,128,61,0.14)",
    "0 16px 40px rgba(21,128,61,0.15)",
    "0 18px 44px rgba(21,128,61,0.16)",
    "0 20px 48px rgba(21,128,61,0.17)",
    "0 22px 52px rgba(21,128,61,0.18)",
    "0 24px 56px rgba(21,128,61,0.18)",
    "0 26px 60px rgba(21,128,61,0.19)",
    "0 28px 64px rgba(21,128,61,0.19)",
    "0 30px 68px rgba(21,128,61,0.20)",
    "0 32px 72px rgba(21,128,61,0.20)",
    "0 34px 76px rgba(21,128,61,0.21)",
    "0 36px 80px rgba(21,128,61,0.21)",
    "0 38px 84px rgba(21,128,61,0.22)",
    "0 40px 88px rgba(21,128,61,0.22)",
    "0 42px 92px rgba(21,128,61,0.23)",
    "0 44px 96px rgba(21,128,61,0.23)",
    "0 46px 100px rgba(21,128,61,0.24)",
  ],

  components: {
    /* ---------------- BASELINE ---------------- */
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: "antialiased" },
        body: {
          backgroundColor: SURFACE_ALT,
          color: INK,
          scrollbarColor: `${alpha(INK_4, 0.4)} transparent`,
        },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(INK_4, 0.3),
          borderRadius: 1,
        },
      },
    },

    /* ---------------- BUTTON ---------------- */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 1,
          paddingInline: 20,
          paddingBlock: 11,
          fontFamily: '"Sora", sans-serif',
          fontWeight: 700,
          textTransform: "none",
          transition: "all 200ms cubic-bezier(.2,.8,.2,1)",
          "&:active": { transform: "scale(0.98)" },
        },
        sizeSmall: { paddingInline: 14, paddingBlock: 7, fontSize: 13 },
        sizeLarge: { paddingInline: 24, paddingBlock: 14, fontSize: 15 },
        containedPrimary: {
          background: `linear-gradient(135deg, ${GREEN_LIGHT} 0%, ${GREEN_ACCENT} 100%)`,
          color: "#fff",
          boxShadow: "0 4px 20px rgba(34,197,94,0.35)",
          "&:hover": {
            boxShadow: "0 8px 28px rgba(34,197,94,0.5)",
            transform: "translateY(-1px)",
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_LIGHT} 100%)`,
          boxShadow: "0 4px 18px rgba(21,128,61,0.30)",
          "&:hover": {
            boxShadow: "0 8px 26px rgba(21,128,61,0.42)",
            transform: "translateY(-1px)",
          },
        },
        containedError: {
          background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
          boxShadow: "0 4px 16px rgba(220,38,38,0.32)",
          "&:hover": { boxShadow: "0 6px 22px rgba(220,38,38,0.45)" },
        },
        outlined: {
          borderWidth: 2,
          borderColor: GREEN,
          color: GREEN,
          backgroundColor: "#fff",
          "&:hover": {
            borderWidth: 2,
            borderColor: GREEN,
            backgroundColor: alpha(GREEN, 0.06),
          },
        },
        text: {
          "&:hover": { backgroundColor: alpha(GREEN, 0.06) },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          transition: "all 200ms ease",
          "&:hover": { backgroundColor: alpha(GREEN, 0.08) },
        },
      },
    },

    /* ---------------- CARD ---------------- */
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 1,
          backgroundColor: "#fff",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          overflow: "hidden",
          transition:
            "transform 220ms cubic-bezier(.2,.8,.2,1), box-shadow 220ms ease, border-color 220ms ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 28px rgba(21,128,61,0.10)",
            borderColor: alpha(GREEN, 0.35),
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          "&:last-child": { paddingBottom: 20 },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: 1 },
        outlined: { borderColor: BORDER },
      },
    },

    /* ---------------- DIALOG (bottom-sheet on mobile) ---------------- */
    MuiDialog: {
      defaultProps: { PaperProps: { elevation: 0 } },
      styleOverrides: {
        paper: {
          borderRadius: 2,
          padding: 0,
          border: "none",
          boxShadow: "0 -8px 48px rgba(0,0,0,0.3)",
          backgroundImage: "none",
          backgroundColor: "#fff",
          overflow: "hidden",
          "@media (max-width:600px)": {
            margin: 0,
            marginTop: "auto",
            width: "100%",
            maxHeight: "92vh",
            borderRadius: "2px 2px 0 0",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(DARK, 0.7),
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Sora", sans-serif',
          fontWeight: 800,
          fontSize: 18,
          padding: "20px 24px 10px",
          color: INK,
          lineHeight: 1.3,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "8px 24px 16px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "14px 20px 20px",
          gap: 10,
          borderTop: `1px solid ${GRAY_50}`,
        },
      },
    },

    /* ---------------- TEXT FIELD ---------------- */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          backgroundColor: GRAY_50,
          fontSize: 14,
          transition: "box-shadow 200ms ease, border-color 200ms ease, background-color 200ms ease",
          "& fieldset": { borderColor: BORDER, borderWidth: 1.5 },
          "&:hover fieldset": { borderColor: alpha(GREEN, 0.5) },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            boxShadow: `0 0 0 3px ${alpha(GREEN, 0.12)}`,
          },
          "&.Mui-focused fieldset": {
            borderColor: GREEN,
            borderWidth: 1.5,
          },
          "&.Mui-error": {
            backgroundColor: "#fff5f5",
            "& fieldset": { borderColor: "#ef4444" },
            "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha("#ef4444", 0.12)}` },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: 13,
          color: INK_3,
          "&.Mui-focused": { color: GREEN },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 4, fontSize: 12, fontWeight: 500 },
      },
    },

    /* ---------------- CHIP ---------------- */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          fontFamily: '"Sora", sans-serif',
          fontWeight: 700,
          fontSize: 11,
          height: 26,
          letterSpacing: 0.3,
        },
        filledSuccess: {
          backgroundColor: alpha(GREEN_ACCENT, 0.18),
          color: GREEN_DARK,
          border: `1px solid ${alpha(GREEN_ACCENT, 0.35)}`,
        },
        filledWarning: {
          backgroundColor: alpha(GOLD, 0.16),
          color: "#92400e",
          border: `1px solid ${alpha(GOLD, 0.35)}`,
        },
        filledError: {
          backgroundColor: alpha("#ef4444", 0.14),
          color: "#b91c1c",
          border: `1px solid ${alpha("#ef4444", 0.3)}`,
        },
        filledInfo: {
          backgroundColor: alpha("#0ea5e9", 0.12),
          color: "#0369a1",
        },
        filledPrimary: {
          backgroundColor: alpha(GREEN, 0.12),
          color: GREEN_DARK,
        },
        outlined: { borderColor: BORDER },
      },
    },

    /* ---------------- BOTTOM NAVIGATION ---------------- */
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 72,
          backgroundColor: alpha("#ffffff", 0.96),
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderTop: `1px solid ${BORDER}`,
          boxShadow: "0 -6px 24px rgba(21,128,61,0.08)",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1100,
          paddingInline: 8,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: INK_4,
          fontWeight: 600,
          minWidth: 0,
          paddingTop: 12,
          gap: 2,
          transition: "color 200ms ease, transform 200ms ease",
          "&.Mui-selected": {
            color: GREEN,
            transform: "translateY(-2px)",
            "& .MuiSvgIcon-root": {
              filter: `drop-shadow(0 4px 10px ${alpha(GREEN_ACCENT, 0.5)})`,
            },
          },
          "& .MuiBottomNavigationAction-label": {
            fontFamily: '"Sora", sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.2,
            "&.Mui-selected": { fontSize: 11 },
          },
        },
      },
    },

    /* ---------------- LIST / TABS / DIVIDER ---------------- */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          marginBottom: 4,
          transition: "background-color 200ms ease",
          "&:hover": { backgroundColor: alpha(GREEN, 0.06) },
          "&.Mui-selected": {
            backgroundColor: alpha(GREEN, 0.1),
            "&:hover": { backgroundColor: alpha(GREEN, 0.14) },
          },
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: BORDER } } },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Sora", sans-serif',
          textTransform: "none",
          fontWeight: 700,
          fontSize: 14,
          minHeight: 44,
          color: INK_3,
          "&.Mui-selected": { color: GREEN_DARK },
        },
      },
    },

    MuiAvatar: { styleOverrides: { root: { fontWeight: 700 } } },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(DARK, 0.94),
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 1,
          padding: "6px 10px",
        },
        arrow: { color: alpha(DARK, 0.94) },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 1, fontWeight: 500 },
        standardSuccess: {
          backgroundColor: alpha(GREEN_ACCENT, 0.12),
          color: GREEN_DARK,
        },
        standardError: { backgroundColor: alpha("#ef4444", 0.1), color: "#b91c1c" },
        standardInfo: { backgroundColor: alpha("#0ea5e9", 0.1) },
        standardWarning: { backgroundColor: alpha(GOLD, 0.14), color: "#92400e" },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 1, height: 6, backgroundColor: "#f1f5f9" },
        bar: {
          borderRadius: 1,
          background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: { color: GREEN },
      },
    },
  },
});

export default theme;